import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchClusters,
  fetchEvents,
  createEventFromPhotos,
  addPhotosToEvent,
  thumbUrl,
  type ClusterMode,
  type EventSummary,
  type LooseCluster,
} from "./api.js";
import { navigate, goBack } from "./router.js";
import { useAuth } from "./auth.js";
import { useCoarsePointer } from "./shared.js";
import { DestinationPicker, noteRecent } from "./destination-picker.js";
import { PhotoViewer, type ViewerPhoto } from "./photo-viewer.js";

/**
 * « À trier » : les photos qui ne sont dans aucun album, et les gestes pour les ranger.
 */
const MODE_KEY = "diapo.clusterMode";

function readMode(): ClusterMode {
  try {
    return localStorage.getItem(MODE_KEY) === "similar" ? "similar" : "sheet";
  } catch {
    return "sheet";
  }
}

export function LooseView({ photoId, onBack }: { photoId: string | null; onBack: () => void }) {
  const [clusters, setClusters] = useState<LooseCluster[] | null>(null);
  const [total, setTotal] = useState(0);
  // Le mode de regroupement survit à la navigation : on trie par longues séances, et
  // repartir du mode par défaut à chaque retour serait pénible.
  const [mode, setMode] = useState<ClusterMode>(readMode);
  const [grouped, setGrouped] = useState(0);
  const [embedded, setEmbedded] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState("");
  const [dateApprox, setDateApprox] = useState("");
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [picking, setPicking] = useState(false);
  const lastIdRef = useRef<string | null>(null);
  const coarse = useCoarsePointer();
  const { state: authState } = useAuth();
  /** Regarder est ouvert à tous ; ranger demande d'être éditeur. */
  const peutRanger = authState.status === "authed" && authState.isEditor;

  const load = useCallback(() => {
    fetchClusters(mode).then((d) => {
      setClusters(d.clusters);
      setTotal(d.total);
      setGrouped(d.grouped);
      setEmbedded(d.embedded);
    });
    // Les destinations changent au fil du tri (nouveaux brouillons, compteurs).
    fetchEvents().then((d) => setEvents(d.events)).catch(() => {});
  }, [mode]);
  useEffect(() => load(), [load]);

  const changeMode = (m: ClusterMode): void => {
    setClusters(null);
    setSelected(new Set());
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* localStorage indisponible : le choix ne survivra pas, sans casser */
    }
  };

  const flatIds = clusters ? clusters.flatMap((c) => c.photoIds) : [];
  const flatViewer: ViewerPhoto[] = clusters
    ? clusters.flatMap((c) => c.photoIds.map((id, i) => ({ id, scanName: c.scanNames[i] })))
    : [];
  const viewerIdx = photoId ? flatIds.indexOf(photoId) : -1;

  const toggle = (id: string, shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastIdRef.current) {
        const a = flatIds.indexOf(lastIdRef.current);
        const b = flatIds.indexOf(id);
        if (a !== -1 && b !== -1) {
          for (let i = Math.min(a, b); i <= Math.max(a, b); i++) next.add(flatIds[i]!);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastIdRef.current = id;
  };

  /** Bascule une série entière : tout sélectionner, ou tout retirer si elle l'est déjà. */
  const toggleSeries = (ids: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const all = ids.every((id) => next.has(id));
      for (const id of ids) {
        if (all) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    lastIdRef.current = ids[ids.length - 1] ?? null;
  };

  const reset = () => {
    setSelected(new Set());
    setTitle("");
    setDateApprox("");
    lastIdRef.current = null;
  };

  // Sans titre on crée un brouillon : grouper maintenant, nommer plus tard.
  const create = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await createEventFromPhotos(title.trim(), dateApprox.trim() || null, [...selected]);
      reset();
      load();
    } finally {
      setBusy(false);
    }
  };

  /**
   * Entrée valide depuis le titre ou la date, exactement comme le bouton visible —
   * « Créer l'album » si un titre est saisi, « Grouper sans nom » sinon. On nomme
   * souvent une rafale d'albums d'affilée : aller chercher le bouton à la souris entre
   * chaque casse le rythme, alors que la main est déjà sur le clavier.
   */
  const onKeyCreate = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter" || busy) return;
    e.preventDefault();
    void create();
  };

  const addTo = async (eventId: string) => {
    if (selected.size === 0) return;
    setBusy(true);
    setPicking(false);
    try {
      await addPhotosToEvent(eventId, [...selected]);
      noteRecent(eventId);
      reset();
      load();
    } finally {
      setBusy(false);
    }
  };

  if (!clusters) return <div className="gallery__loading">Chargement…</div>;

  return (
    <div className="gallery" data-testid="loose-view">
      <button className="back" onClick={onBack}>‹ Albums</button>
      <h2 className="gallery__title">
        À trier <span className="gallery__count">{total}</span>
      </h2>
      <p className="loose__hint">
        {!peutRanger ? (
          <>
            Les photos qui n'ont pas encore trouvé leur album. Clique pour en ouvrir une en
            grand. Le rangement est réservé à quelques personnes de la famille.
          </>
        ) : coarse ? (
          <>
            Touche une photo pour l'ouvrir en grand, ou le cercle en haut à droite pour la
            sélectionner. <strong>Tout</strong> sélectionne une série entière. Tu peux{" "}
            <strong>grouper sans nommer</strong> : le titre viendra plus tard, en session.
          </>
        ) : (
          <>
            Clique une photo pour l'ouvrir en grand. Survole et coche l'icône (en haut à
            droite) pour sélectionner — <strong>Maj+clic</strong> pour une plage,
            <strong> Tout</strong> pour une série entière. Tu peux{" "}
            <strong>grouper sans nommer</strong> : le titre viendra plus tard, en session.
          </>
        )}
      </p>

      {/*
        Deux façons de regrouper, et elles ne servent pas à la même chose. Le libellé dit
        franchement ce que chacune vaut : la ressemblance visuelle ne rapproche que des
        photos presque identiques (58 sur 1000, mesuré) — deux photos d'un même événement
        ne se ressemblent pas au sens du dhash. Annoncer « groupé par similarité » sans ce
        chiffre laisserait croire à un classement qui n'existe pas.
      */}
      <div className="lmode" data-testid="loose-mode">
        <button
          className={`lmode__btn ${mode === "sheet" ? "lmode__btn--on" : ""}`}
          onClick={() => changeMode("sheet")}
          data-testid="mode-sheet"
        >
          Par planche de scan
        </button>
        <button
          className={`lmode__btn ${mode === "similar" ? "lmode__btn--on" : ""}`}
          onClick={() => changeMode("similar")}
          data-testid="mode-similar"
        >
          Par ressemblance
        </button>
        <span className="lmode__hint">
          {mode === "sheet"
            ? "Les séparateurs marquent les séries de scan — souvent un même événement."
            : embedded < total
              ? `Empreintes calculées pour ${embedded} photos sur ${total} — relance « npm run embed:photos » pour les autres.`
              : `${grouped} photos sur ${total} ont au moins une voisine qui montre la même chose.`}
        </span>
      </div>

      {clusters.map((c, ci) => (
        <section key={ci} className="loose__cluster">
          <div className="loose__sep">
            <span>
              {c.singles
                ? `sans voisin ressemblant · ${c.photoIds.length}`
                : mode === "similar"
                  ? `groupe ${ci + 1} · ${c.photoIds.length} photos qui montrent la même chose`
                  : `série ${ci + 1} · planches ${c.sheetFrom}–${c.sheetTo} · ${c.photoIds.length}`}
            </span>
            {/* Équivalent tactile du Maj+clic : la série est la plage naturelle. */}
            {peutRanger && (
              <button
                className="loose__all"
                onClick={() => toggleSeries(c.photoIds)}
                data-testid="loose-all"
              >
                {c.photoIds.every((id) => selected.has(id)) ? "Aucune" : "Tout"}
              </button>
            )}
          </div>
          <div className="grid grid--photos">
            {c.photoIds.map((id) => (
              <div
                key={id}
                className={`tile loose__tile ${selected.has(id) ? "loose__tile--sel" : ""}`}
                onClick={() => navigate({ kind: "loose", photoId: id })}
                data-testid="loose-tile"
              >
                <img src={thumbUrl(id)} alt="" loading="lazy" />
                {peutRanger && (
                  <button
                    className={`loose__select ${selected.has(id) ? "loose__select--on" : ""}`}
                    data-testid="loose-select"
                    aria-label="Sélectionner"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(id, e.shiftKey);
                    }}
                  >
                    {selected.has(id) ? "✓" : ""}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      {selected.size > 0 && (
        <div className="loosebar" data-testid="loose-bar">
          <span className="loosebar__count">{selected.size} sélectionnée{selected.size > 1 ? "s" : ""}</span>
          <input
            className="loosebar__title"
            placeholder="Titre — facultatif"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={onKeyCreate}
            data-testid="loose-title"
          />
          <input
            className="loosebar__date"
            placeholder="Date"
            value={dateApprox}
            onChange={(e) => setDateApprox(e.target.value)}
            onKeyDown={onKeyCreate}
            data-testid="loose-date"
          />
          <button
            className="loosebar__create"
            disabled={busy}
            onClick={() => void create()}
            data-testid="loose-create"
          >
            {title.trim() ? "Créer l'album" : "Grouper sans nom"}
          </button>
          <span className="loosebar__or">ou</span>
          <button
            className="loosebar__create"
            disabled={busy}
            onClick={() => setPicking(true)}
            data-testid="loose-add"
          >
            Ranger dans…
          </button>
          <button className="loosebar__clear" onClick={reset}>
            Annuler
          </button>
        </div>
      )}

      {picking && (
        <DestinationPicker
          events={events}
          photoIds={flatIds.filter((id) => selected.has(id))}
          onPick={(id) => void addTo(id)}
          onClose={() => setPicking(false)}
        />
      )}

      {viewerIdx !== -1 && (
        <PhotoViewer
          photos={flatViewer}
          index={viewerIdx}
          eventId={null}
          onIndex={(i) =>
            navigate({ kind: "loose", photoId: flatViewer[i]!.id }, { replace: true })
          }
          onClose={() => goBack({ kind: "loose", photoId: null })}
          onAssigned={() => {
            // La photo n'est plus dans le vrac : on referme et on rafraîchit.
            navigate({ kind: "loose", photoId: null }, { replace: true });
            load();
          }}
        />
      )}
    </div>
  );
}
